/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import { AfterContentInit, ChangeDetectionStrategy, Component, ContentChildren, Input, OnDestroy, Optional, QueryList } from '@angular/core';
import { FormControl, FormControlDirective } from '@angular/forms';
import { contentChildren$ } from '@ng-falcon/+utils/content-children$';
import { NgfControlErrorComponent } from '@ng-falcon/ngf-form/ngf-control/ngf-control-error/ngf-control-error.component';
import { NgfFormService } from '@ng-falcon/ngf-form/ngf-form.service';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, share } from 'rxjs/operators';

@Component({
    selector: 'ngf-control',
    templateUrl: './ngf-control.component.html',
    styleUrls: ['./ngf-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NgfControlComponent implements AfterContentInit, OnDestroy {

  @ContentChildren(FormControlDirective, { descendants: true })
  readonly formControlDirectives!: QueryList<FormControlDirective>;
  @ContentChildren(NgfControlErrorComponent)
  readonly errors: QueryList<NgfControlErrorComponent>;

  formControls$: Observable<readonly FormControl[]>;
  hasErrors$: Observable<boolean>;

  private sub = Subscription.EMPTY;

  // default behavior of ngfFormControl is to reset
  // to pristine if no value is provided. With this 
  // flag, the default value behavior is used instead
  // when auto resetting in ngOnDestroy
  @Input()
  resetToDefault = false;

  constructor(@Optional() private ngfForm: NgfFormService | null) {}

  ngAfterContentInit(): void {
    if (this.ngfForm?.$ref.autoEnableControlsWhenUsed) {
      this.formControlDirectives.forEach(directive => {
        directive.control.enable();
      });
    }

    this.formControls$ = contentChildren$(this.formControlDirectives).pipe(
      map(directives => directives.map(it => it.control)),
      distinctUntilChanged(),
      share({ connector: () => new ReplaySubject(1) })
    );

    this.hasErrors$ = contentChildren$(this.errors).pipe(
      map(errors => errors.length > 0),
      distinctUntilChanged()
    );
  }

  ngOnDestroy(): void {
    if (this.ngfForm?.$ref.autoEnableControlsWhenUsed) {
      this.formControlDirectives.forEach(directive => {
        directive.control.disable();
        if (this.resetToDefault) {
          directive.control.reset(directive.control.defaultValue);
        }
        directive.control.reset();
      });
    }

    this.sub.unsubscribe();
  }

}
